// screens/AgreementSpread.tsx — K3: one Agreement, whole. Hero (title, kind,
// version, status, acceptance line), body (the two-column consideration
// table — Vowric provides · the client provides — plus the end condition),
// aside (the actions: add a term, mark sent, record acceptance, mark a term
// fulfilled), each with its receipt (c4-agreement.md's web spec).
//
// Reuses the shared spread grid (spread.css, the A4/WorkOrder contract) —
// this screen owns no layout of its own, only its own content inside it.
import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  acceptAgreement,
  addAgreementTerm,
  fulfilAgreementTerm,
  getAgreement,
  sendAgreement,
  type AgreementApi,
  type AgreementTermApi,
} from '../lib/api';
import { AGREEMENT_ACCEPTED_VIA, AGREEMENT_TERM_KINDS, AGREEMENT_TERM_KIND_PHRASES, type AgreementTermKind } from '@vowric/shared';
import { BackLink } from '../components/BackLink';
import './spread.css';

export interface AgreementSpreadProps {
  tenantId: string;
  id: string;
  backLabel?: string;
  onBack: () => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; agreement: AgreementApi; terms: AgreementTermApi[] };

/** The machine voice's plain phrase for a term — never the enum key
 * (c4's copy rule). `other` reads by its own description. */
function termPhrase(kind: string, description: string): string {
  if (kind === 'other') return description;
  return AGREEMENT_TERM_KIND_PHRASES[kind as Exclude<AgreementTermKind, 'other'>] ?? kind;
}

function TermRow({ term, onFulfil, busy }: { term: AgreementTermApi; onFulfil: () => void; busy: boolean }) {
  return (
    <div class="sp__row" key={term.id}>
      <div class="sp__row-top">
        <span class="sp__row-name">{termPhrase(term.kind, term.description)}</span>
        {term.fulfilledOn ? (
          <span class="sp__chip sp__chip--accent">fulfilled {term.fulfilledOn}</span>
        ) : term.dueOn ? (
          <button type="button" class="sp__pill" disabled={busy} onClick={onFulfil}>
            due {term.dueOn} · mark fulfilled
          </button>
        ) : (
          <span class="sp__chip">no date</span>
        )}
      </div>
      <span class="sp__row-sub">
        {term.valueText ? `${term.valueText} · ` : ''}
        {term.owner ? `owner: ${term.owner}` : ''}
        {term.expiresOn ? ` · expires ${term.expiresOn}` : ''}
      </span>
    </div>
  );
}

export function AgreementSpread({ tenantId, id, backLabel, onBack }: AgreementSpreadProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const [addingTerm, setAddingTerm] = useState(false);
  const [termSide, setTermSide] = useState<'vowric' | 'client'>('client');
  const [termKind, setTermKind] = useState<string>(AGREEMENT_TERM_KINDS[0]!);
  const [termDescription, setTermDescription] = useState('');
  const [termValue, setTermValue] = useState('');
  const [termOwner, setTermOwner] = useState('');
  const [termDue, setTermDue] = useState('');
  const [termExpires, setTermExpires] = useState('');

  const [accepting, setAccepting] = useState(false);
  const [acceptedOn, setAcceptedOn] = useState('');
  const [acceptedBy, setAcceptedBy] = useState('');
  const [acceptedVia, setAcceptedVia] = useState<string>(AGREEMENT_ACCEPTED_VIA[0]!);

  function refresh() {
    setLoad({ phase: 'loading' });
    return getAgreement(tenantId, id)
      .then(({ agreement, terms }) => setLoad({ phase: 'ready', agreement, terms }))
      .catch((err: unknown) => {
        setLoad({
          phase: 'error',
          message: err instanceof ApiError && err.status === 404 ? 'agreement not found'
            : err instanceof ApiError ? err.message : 'this agreement could not be read',
        });
      });
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, id]);

  async function submitTerm() {
    if (!termDescription.trim()) return;
    setBusy('term');
    setNote(null);
    try {
      await addAgreementTerm(tenantId, id, {
        side: termSide,
        kind: termKind,
        description: termDescription.trim(),
        valueText: termValue.trim() || null,
        owner: termOwner.trim() || null,
        dueOn: termDue || null,
        expiresOn: termExpires || null,
      });
      setAddingTerm(false);
      setTermDescription('');
      setTermValue('');
      setTermOwner('');
      setTermDue('');
      setTermExpires('');
      await refresh();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'the term was not added');
    } finally {
      setBusy(null);
    }
  }

  async function markSent() {
    setBusy('send');
    setNote(null);
    try {
      await sendAgreement(tenantId, id);
      await refresh();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'not marked sent');
    } finally {
      setBusy(null);
    }
  }

  async function submitAcceptance() {
    if (!acceptedOn || !acceptedBy.trim() || load.phase !== 'ready' || !load.agreement.offeredRevision) return;
    setBusy('accept');
    setNote(null);
    try {
      const result = await acceptAgreement(tenantId, id, {
        revision: load.agreement.offeredRevision, acceptedOn, acceptedBy: acceptedBy.trim(), via: acceptedVia,
      });
      setAccepting(false);
      setNote(
        `accepted revision ${result.agreement.acceptedRevision} · ${result.agreement.title} · ` +
        `${result.commitmentsOpened} obligation${result.commitmentsOpened === 1 ? '' : 's'} opened` +
        (result.supersededAgreementId ? ` · superseded a predecessor` : '')
      );
      await refresh();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'the acceptance was not recorded');
    } finally {
      setBusy(null);
    }
  }

  async function fulfilTerm(term: AgreementTermApi) {
    setBusy(term.id);
    setNote(null);
    try {
      await fulfilAgreementTerm(tenantId, id, term.id, { on: new Date().toISOString().slice(0, 10) });
      setNote(`fulfilled · ${termPhrase(term.kind, term.description)}`);
      await refresh();
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'not marked fulfilled');
    } finally {
      setBusy(null);
    }
  }

  if (load.phase === 'loading') {
    return (
      <div class="sp">
        <BackLink label={backLabel ?? 'Agreements'} onClick={onBack} />
      </div>
    );
  }
  if (load.phase === 'error') {
    return (
      <div class="sp">
        <BackLink label={backLabel ?? 'Agreements'} onClick={onBack} />
        <div class="sp__say">
          This agreement could not be read.
          <br />
          <span class="sp__say-soft">{load.message}</span>
        </div>
      </div>
    );
  }

  const { agreement, terms } = load;
  const vowricTerms = terms.filter((t) => t.side === 'vowric');
  const clientTerms = terms.filter((t) => t.side === 'client');
  const editable = agreement.status === 'draft' || agreement.status === 'sent';

  return (
    <div class="sp">
      <BackLink label={backLabel ?? 'Agreements'} onClick={onBack} />

      <div class="sp__grid">
        <div class="sp__hero">
          <div class="sp__label">
            <span>{agreement.kind.replace(/_/g, ' ')}</span>
            <span>v{agreement.version}</span>
            <span class={`sp__chip${agreement.status === 'accepted' ? ' sp__chip--accent' : ''}`}>{agreement.status}</span>
          </div>
          <h1 class="sp__title">{agreement.title}</h1>
          {agreement.acceptedOn ? (
            <p class="sp__sub">
              accepted {agreement.acceptedOn} by {agreement.acceptedBy} via {agreement.acceptedVia}
              {agreement.acceptedRevision ? ` · revision ${agreement.acceptedRevision}` : ''}
              {agreement.acceptedContentSha256 ? ` · doc ${agreement.acceptedContentSha256.slice(-8)}` : ''}
            </p>
          ) : null}
          {agreement.endCondition ? <p class="sp__sub">Ends: {agreement.endCondition}</p> : null}
        </div>

        <div class="sp__aside">
          <div class="sp__sec">Actions</div>
          <div class="sp__acts">
            {editable ? (
              <button type="button" class="sp__act" disabled={busy !== null} onClick={() => setAddingTerm(true)}>
                Add a term
              </button>
            ) : null}
            {agreement.status === 'draft' ? (
              <button type="button" class="sp__act" disabled={busy !== null} onClick={() => void markSent()}>
                Mark sent
              </button>
            ) : null}
            {editable && agreement.offeredRevision ? (
              <button type="button" class="sp__act" disabled={busy !== null} onClick={() => setAccepting(true)}>
                Record acceptance
              </button>
            ) : null}
          </div>
          {editable && agreement.offeredRevision ? (
            <p class="sp__sub">
              accepting revision {agreement.offeredRevision}, offered {agreement.sentAt ?? '(sending)'}
            </p>
          ) : null}

          {addingTerm ? (
            <div class="sp__standing">
              <select value={termSide} disabled={busy === 'term'} onChange={(e) => setTermSide((e.target as HTMLSelectElement).value as 'vowric' | 'client')}>
                <option value="client">the client provides</option>
                <option value="vowric">Vowric provides</option>
              </select>
              <select value={termKind} disabled={busy === 'term'} onChange={(e) => setTermKind((e.target as HTMLSelectElement).value)}>
                {AGREEMENT_TERM_KINDS.map((k) => (
                  <option value={k} key={k}>{k === 'other' ? 'other' : AGREEMENT_TERM_KIND_PHRASES[k as Exclude<AgreementTermKind, 'other'>]}</option>
                ))}
              </select>
              <input type="text" placeholder="Description" value={termDescription} disabled={busy === 'term'}
                onInput={(e) => setTermDescription((e.target as HTMLInputElement).value)} />
              <input type="text" placeholder="Value (e.g. 50% after go-live)" value={termValue} disabled={busy === 'term'}
                onInput={(e) => setTermValue((e.target as HTMLInputElement).value)} />
              <input type="text" placeholder="Owner" value={termOwner} disabled={busy === 'term'}
                onInput={(e) => setTermOwner((e.target as HTMLInputElement).value)} />
              <input type="date" placeholder="Due" value={termDue} disabled={busy === 'term'}
                onInput={(e) => setTermDue((e.target as HTMLInputElement).value)} />
              <input type="date" placeholder="Expires" value={termExpires} disabled={busy === 'term'}
                onInput={(e) => setTermExpires((e.target as HTMLInputElement).value)} />
              <div class="sp__acts">
                <button type="button" class="sp__act" disabled={busy === 'term' || !termDescription.trim()} onClick={() => void submitTerm()}>Add</button>
                <button type="button" class="sp__act" disabled={busy === 'term'} onClick={() => setAddingTerm(false)}>Never mind</button>
              </div>
            </div>
          ) : null}

          {accepting ? (
            <div class="sp__standing">
              <input type="date" value={acceptedOn} disabled={busy === 'accept'} onInput={(e) => setAcceptedOn((e.target as HTMLInputElement).value)} />
              <input type="text" placeholder="Accepted by (name as written)" value={acceptedBy} disabled={busy === 'accept'}
                onInput={(e) => setAcceptedBy((e.target as HTMLInputElement).value)} />
              <select value={acceptedVia} disabled={busy === 'accept'} onChange={(e) => setAcceptedVia((e.target as HTMLSelectElement).value)}>
                {AGREEMENT_ACCEPTED_VIA.map((v) => <option value={v} key={v}>{v}</option>)}
              </select>
              <div class="sp__acts">
                <button type="button" class="sp__act" disabled={busy === 'accept' || !acceptedOn || !acceptedBy.trim()} onClick={() => void submitAcceptance()}>Record</button>
                <button type="button" class="sp__act" disabled={busy === 'accept'} onClick={() => setAccepting(false)}>Never mind</button>
              </div>
            </div>
          ) : null}

          {note ? <div class="sp__note">{note}</div> : null}
        </div>

        <div class="sp__body">
          <div class="sp__sec">Vowric provides</div>
          {vowricTerms.length === 0 ? (
            <div class="sp__empty">Nothing on this side yet.</div>
          ) : (
            vowricTerms.map((t) => (
              <TermRow key={t.id} term={t} busy={busy === t.id} onFulfil={() => void fulfilTerm(t)} />
            ))
          )}

          <div class="sp__sec">The client provides</div>
          {clientTerms.length === 0 ? (
            <div class="sp__empty">Nothing on this side yet.</div>
          ) : (
            clientTerms.map((t) => (
              <TermRow key={t.id} term={t} busy={busy === t.id} onFulfil={() => void fulfilTerm(t)} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
